import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import MoreVertRoundedIcon from "@mui/icons-material/MoreVertRounded";
import { IconButton, ListItemIcon, ListItemText, Menu, MenuItem } from "@mui/material";
import { type FC, type MouseEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "../../../contexts/SnackbarContext";
import { useCopyToClipboard } from "../../../hooks/useCopyToClipboard";
import { FONT_SIZE } from "../../../theme/tokens";

/** Default `testIdPrefix` — matches the outer grid's own row menu, unchanged for that call site. */
const DEFAULT_TEST_ID_PREFIX = "grouped-activity-log-row-menu";

type Props = {
	/** The api_request_id this row's menu copies — the outer grid's own group id, or a detail-grid
	 * lineage row's own (possibly-differing) id. Narrowed from the whole row so this menu is reusable
	 * for any row that carries an api_request_id, not just the outer grid's `GroupedActivityLogEntry`. */
	apiRequestId: string;
	/** Overrides the `data-testid` prefix for both the icon button and its copy-menu-item (default
	 * `"grouped-activity-log-row-menu"`). A detail-grid lineage row's `apiRequestId` belongs to a
	 * DIFFERENT transaction that is very likely also visible as its own top-level row in the same
	 * (default 30-day) filter window — without a distinct prefix here, that row's own outer-grid menu
	 * and this lineage-row menu would render the exact same testid twice in the DOM. Callers rendering
	 * more than one instance of this menu per apiRequestId in the page MUST pass a scoped prefix. */
	testIdPrefix?: string;
};

/**
 * Per-row overflow (⋯) menu for activity-log grids (the outer grouped grid, and lineage rows in the
 * detail grid). Single entry point for row-level actions — new options (e.g. "view details") extend
 * this menu rather than growing more one-off columns.
 */
export const GroupedActivityLogRowMenu: FC<Props> = ({ apiRequestId, testIdPrefix = DEFAULT_TEST_ID_PREFIX }) => {
	const { t } = useTranslation();
	const { copyToClipboard } = useCopyToClipboard();
	const { showSnackbar } = useSnackbar();
	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
	const open = Boolean(anchorEl);
	const close = () => setAnchorEl(null);

	const handleCopyApiRequestId = async () => {
		close();
		const success = await copyToClipboard(apiRequestId);
		showSnackbar(
			success ? t("activityLog.grouped.rowMenu.copyApiRequestId.success") : t("activityLog.grouped.rowMenu.copyApiRequestId.error"),
			success ? "success" : "error",
		);
	};

	return (
		<>
			<IconButton
				size="small"
				aria-label={t("activityLog.grouped.rowMenu.aria")}
				aria-haspopup="menu"
				aria-expanded={open}
				onClick={(event: MouseEvent<HTMLElement>) => {
					event.stopPropagation();
					setAnchorEl(event.currentTarget);
				}}
				data-testid={`${testIdPrefix}-${apiRequestId}`}
			>
				<MoreVertRoundedIcon fontSize="small" />
			</IconButton>
			<Menu
				anchorEl={anchorEl}
				open={open}
				onClose={close}
				anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
				transformOrigin={{ vertical: "top", horizontal: "right" }}
			>
				<MenuItem onClick={handleCopyApiRequestId} data-testid={`${testIdPrefix}-copy-api-request-id-${apiRequestId}`}>
					<ListItemIcon>
						<ContentCopyOutlinedIcon fontSize="small" />
					</ListItemIcon>
					<ListItemText primary={t("activityLog.grouped.rowMenu.copyApiRequestId")} slotProps={{ primary: { sx: { fontSize: FONT_SIZE.SECONDARY } } }} />
				</MenuItem>
			</Menu>
		</>
	);
};
